import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const BORDER_COLOR = [140, 29, 26]; // #8c1d1a
const HEADER_BG = [240, 230, 210]; // #f0e6d2

/**
 * Optimally weighted column distribution summing to exactly 100%.
 * Allocates extra breathing room to tax, amounts, and payments to prevent digit wrapping.
 */
const COLS = [
  { key: "name", label: "NAME", w: 8, align: "left" },
  { key: "invoice_no", label: "INVOICE NO", w: 5.5, align: "left" },
  { key: "invoice_date", label: "DATE", w: 5, align: "center" },
  { key: "invoice_value", label: "TOTAL AMT", w: 6.5, align: "right", kind: "int" },
  { key: "taxable_value", label: "TAXABLE VALUE", w: 7.5, align: "right", kind: "money" },
  { key: "sgst", label: "SGST", w: 4.5, align: "center", kind: "money" },
  { key: "cgst", label: "CGST", w: 4.5, align: "center", kind: "money" },
  { key: "igst", label: "IGST", w: 4, align: "center", kind: "money" },
  { key: "total_tax", label: "TOTAL", w: 5.5, align: "center", kind: "money" },
  { key: "gold_gm", label: "GOLD", w: 4, align: "center", kind: "gm" },
  { key: "silver_gm", label: "SILVER", w: 4, align: "center", kind: "gm" },
  { key: "cash", label: "CASH", w: 5.5, align: "center", kind: "money" },
  { key: "upi", label: "UPI", w: 5.5, align: "center", kind: "money" },
  { key: "bank", label: "BANK", w: 5.5, align: "center", kind: "money" },
  { key: "card", label: "CARD", w: 4.5, align: "center", kind: "money" },
  { key: "old_gold", label: "OLD GOLD", w: 10, align: "center", kind: "old_metal" },
  { key: "old_silver", label: "OLD SILVER", w: 10, align: "center", kind: "old_metal" },
];

function formatOldMetal(amount, gm) {
  const amt = Number(amount) || 0;
  const grams = Number(gm) || 0;
  if (amt === 0 && grams === 0) return "-";
  const amtText =
    amt === 0
      ? "0.00"
      : amt.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  return `${amtText} (${grams.toFixed(3)})`;
}

function formatCell(value, kind) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "-";
  if (kind === "int") return Math.round(n).toLocaleString("en-IN");
  if (kind === "gm") return n.toFixed(3);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatColValue(col, row) {
  if (!row) return "-";
  if (col.kind === "old_metal") {
    if (col.key === "old_gold") {
      return formatOldMetal(row.old_gold_amt, row.old_gold_gm);
    }
    if (col.key === "old_silver") {
      return formatOldMetal(row.old_silver_amt, row.old_silver_gm);
    }
    return "-";
  }
  const raw = row[col.key];
  if (col.kind != null) return formatCell(raw, col.kind);
  if (raw == null || raw === "") return "-";
  return String(raw);
}

function getFormattedGeneratedOn() {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();
  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
}

/**
 * Generates Sales Ledger PDF Blob using high-performance jsPDF + autotable.
 * Ensures all numbers, dates, and amounts fit strictly on a single line (no wrapped digits).
 */
export async function generateSalesLedgerPdfBlob({ ledgerData = [], summaryData = null }) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth(); // 841.89 pt
  const marginX = 10;
  const availableTableWidth = pageWidth - marginX * 2; // 821.89 pt

  // Multi-tier header matching the exact layout
  const head = [
    [
      { content: "NAME", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
      { content: "INVOICE DETAILS", colSpan: 3, styles: { halign: "center", valign: "middle" } },
      { content: "TAXABLE VALUE", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
      { content: "AMOUNT OF TAX", colSpan: 4, styles: { halign: "center", valign: "middle" } },
      { content: "WEIGHT (GM)", colSpan: 2, styles: { halign: "center", valign: "middle" } },
      { content: "PAYMENT", colSpan: 6, styles: { halign: "center", valign: "middle" } },
    ],
    [
      { content: "INVOICE NO", styles: { halign: "left" } },
      { content: "DATE", styles: { halign: "center" } },
      { content: "TOTAL AMT", styles: { halign: "right" } },
      { content: "SGST", styles: { halign: "center" } },
      { content: "CGST", styles: { halign: "center" } },
      { content: "IGST", styles: { halign: "center" } },
      { content: "TOTAL", styles: { halign: "center" } },
      { content: "GOLD", styles: { halign: "center" } },
      { content: "SILVER", styles: { halign: "center" } },
      { content: "CASH", styles: { halign: "center" } },
      { content: "UPI", styles: { halign: "center" } },
      { content: "BANK", styles: { halign: "center" } },
      { content: "CARD", styles: { halign: "center" } },
      { content: "OLD GOLD", styles: { halign: "center" } },
      { content: "OLD SILVER", styles: { halign: "center" } },
    ],
  ];

  // Map rows
  const body = ledgerData.map((row) =>
    COLS.map((col) => formatColValue(col, row))
  );

  // Column width calculations & alignments
  const columnStyles = {};
  COLS.forEach((col, idx) => {
    columnStyles[idx] = {
      cellWidth: (availableTableWidth * col.w) / 100,
      halign: col.align || "left",
    };
  });

  // Footer totals row
  let foot = null;
  if (summaryData) {
    const footRow = COLS.map((col) => {
      const skip =
        col.key === "name" ||
        col.key === "invoice_no" ||
        col.key === "invoice_date";
      return skip ? "" : formatColValue(col, summaryData);
    });
    foot = [footRow];
  }

  autoTable(doc, {
    startY: 28,
    head,
    body,
    foot: foot || undefined,
    showFoot: "lastPage",
    theme: "grid",
    tableWidth: availableTableWidth,
    margin: { top: 28, left: marginX, right: marginX, bottom: 18 },
    styles: {
      font: "helvetica",
      fontSize: 5.5,
      cellPadding: { top: 1.5, bottom: 1.5, left: 1, right: 1 },
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    },
    headStyles: {
      font: "helvetica",
      fontSize: 5.5,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      halign: "center",
      valign: "middle",
      cellPadding: { top: 2, bottom: 2, left: 1, right: 1 },
    },
    footStyles: {
      font: "helvetica",
      fontSize: 5.5,
      fillColor: HEADER_BG,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      lineColor: BORDER_COLOR,
      lineWidth: 0.5,
      valign: "middle",
      cellPadding: { top: 2, bottom: 2, left: 1, right: 1 },
    },
    columnStyles,
    didParseCell: (data) => {
      // Prevent multi-line wrapping in body and footer cells, except for NAME
      if (data.section === "body" || data.section === "foot") {
        const colDef = COLS[data.column.index];
        if (!colDef) return;

        // Allow NAME column to wrap onto multiple lines naturally with clear, readable font
        if (colDef.key === "name") {
          data.cell.styles.overflow = "linebreak";
          data.cell.styles.fontSize = 5.5;
          return;
        }

        const rawText = Array.isArray(data.cell.text)
          ? data.cell.text.join(" ")
          : String(data.cell.text ?? "");

        const colWidth = (availableTableWidth * colDef.w) / 100;
        // Available width inside the cell minus horizontal padding
        const maxTextWidth = colWidth - 2.2;

        const isBold = data.section === "foot";
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        let fontSize = data.cell.styles.fontSize || 5.5;
        doc.setFontSize(fontSize);
        let measuredWidth = doc.getTextWidth(rawText);

        // Smart auto-scale: decrease font size incrementally until the entire value fits cleanly on 1 line
        while (measuredWidth > maxTextWidth && fontSize > 3.6) {
          fontSize -= 0.2;
          doc.setFontSize(fontSize);
          measuredWidth = doc.getTextWidth(rawText);
        }

        data.cell.styles.fontSize = fontSize;
        data.cell.styles.overflow = "hidden";
        // Suppress word-wrapping on spaces or brackets
        data.cell.text = [rawText];
      }
    },
    didDrawPage: () => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
      doc.text("Sales Ledger Report", marginX, 18);
    },
  });

  // Add precise "Page X of Y (N records)" on all generated pages
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Page ${page} of ${totalPages} (${ledgerData.length} records)`,
      pageWidth - marginX,
      18,
      { align: "right" }
    );
  }

  // On the last page, print "Generated on: DD-MM-YYYY hh:mm A" after the table ends
  doc.setPage(totalPages);
  const finalY = doc.lastAutoTable?.finalY || 180;
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = Math.min(finalY + 10, pageHeight - 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Generated on: ${getFormattedGeneratedOn()}`,
    pageWidth / 2,
    footerY,
    { align: "center" }
  );

  return doc.output("blob");
}
